import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Sparkles, Loader2, CheckCircle2, AlertCircle, MailCheck } from "lucide-react";
import { orgApi, saveOrganizerToken } from "../lib/organizerApi";
import { useOrganizerAuth } from "../auth/OrganizerAuthContext";

/**
 * Email verification landing — Phase 6.5 / Module 1.
 *
 * Public page. Auto-consumes `?token=...`. On success the backend marks
 * `verified_email_at` and returns a fresh JWT so the user lands logged in.
 */
export default function OrganizerVerifyEmail() {
  const navigate = useNavigate();
  const { refresh } = useOrganizerAuth();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [state, setState] = useState(token ? "loading" : "invalid"); // loading | success | error | invalid
  const [message, setMessage] = useState("");
  const confirmed = useRef(false);

  useEffect(() => {
    if (!token || confirmed.current) return;
    confirmed.current = true;
    (async () => {
      try {
        const { data } = await orgApi.post("/organizer/verify-email/confirm", { token });
        if (data.access_token) {
          saveOrganizerToken(data.access_token);
          await refresh();
        }
        setState("success");
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        if (status === 404) setMessage("Liên kết không hợp lệ. Vui lòng yêu cầu liên kết mới.");
        else if (status === 409) setMessage("Liên kết đã được sử dụng. Email của bạn có thể đã được xác minh.");
        else if (status === 410) setMessage("Liên kết đã hết hạn. Hãy yêu cầu liên kết mới.");
        else setMessage(typeof detail === "string" ? detail : "Không thể xác minh email. Vui lòng thử lại.");
        setState("error");
      }
    })();
  }, [token, refresh]);

  return (
    <div className="min-h-screen bg-linen flex items-center justify-center px-4 py-12" data-testid="organizer-verify-email-page">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-espresso-soft hover:text-espresso text-sm">
          <Sparkles className="w-4 h-4" />
          <span className="tracking-[0.28em] uppercase">Nail Viet Club</span>
        </Link>

        <div className="mt-6 bg-white border border-espresso/10 rounded-3xl p-8 sm:p-10 shadow-sm text-center">
          {state === "loading" && (
            <div data-testid="verify-loading">
              <Loader2 className="w-10 h-10 text-espresso-soft mx-auto animate-spin" />
              <h1 className="mt-4 text-2xl font-serif text-espresso">Đang xác minh email…</h1>
              <p className="mt-2 text-sm text-espresso-soft">Vui lòng đợi trong giây lát.</p>
            </div>
          )}

          {state === "success" && (
            <div data-testid="verify-success">
              <div className="w-14 h-14 mx-auto rounded-full bg-[#E1EDE6] flex items-center justify-center">
                <MailCheck className="w-7 h-7 text-[#2D4A3E]" />
              </div>
              <h1 className="mt-4 text-2xl sm:text-3xl font-serif text-espresso">Email đã được xác minh</h1>
              <p className="mt-2 text-sm text-espresso-soft">
                Cảm ơn bạn! Tài khoản tổ chức của bạn đã sẵn sàng sử dụng đầy đủ tính năng.
              </p>
              <button
                onClick={() => navigate("/organizer/dashboard", { replace: true })}
                data-testid="verify-go-dashboard"
                className="mt-6 inline-flex items-center justify-center gap-2 bg-espresso text-cream rounded-full px-6 py-3 text-sm font-semibold"
              >
                <CheckCircle2 className="w-4 h-4" />
                Vào bảng điều khiển
              </button>
            </div>
          )}

          {(state === "error" || state === "invalid") && (
            <div data-testid="verify-error">
              <div className="w-14 h-14 mx-auto rounded-full bg-rose-100 flex items-center justify-center">
                <AlertCircle className="w-7 h-7 text-rose-700" />
              </div>
              <h1 className="mt-4 text-2xl font-serif text-espresso">Không thể xác minh</h1>
              <p className="mt-2 text-sm text-espresso-soft">
                {message || "Liên kết xác minh thiếu hoặc đã hết hạn."}
              </p>
              <Link
                to="/organizer/login"
                data-testid="verify-go-login"
                className="mt-5 inline-flex items-center justify-center gap-2 border border-espresso/20 text-espresso rounded-full px-5 py-2.5 text-sm font-semibold"
              >
                Đăng nhập để gửi lại
              </Link>
            </div>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-espresso-muted">
          Bạn cần trợ giúp?{" "}
          <Link to="/contact" className="text-espresso underline-offset-2 hover:underline">
            Liên hệ đội ngũ Nail Viet Club
          </Link>
        </p>
      </div>
    </div>
  );
}
